import VisibilityIcon from '@mui/icons-material/Visibility';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Avatar,
  Badge,
  Box,
  Chip,
  Grid,
  IconButton,
  Paper,
  Skeleton,
  Typography,
} from '@mui/material';
import { lighten, useTheme } from '@mui/material/styles';
import useRoles from 'common/hooks/useRoles';
import { capitalize, first, isEmpty, pick, trim } from 'lodash';
import { useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getPartners } from 'store/thunks/partnerThunks';
import { getDuplicatePatients, getPatient, recordAuditEvent } from 'store/thunks/patientThunks';
import stc from 'string-to-color';
import PatientHistoryTrackModal from '../PatientsList/components/PatientHistoryTrackModal';
import DuplicatePatientComparisonDialog from './components/DuplicatePatientComparisonDialog';
import EditPatientButton from './components/EditPatientButton';
import OnboardingButton from './components/OnboardingButton';
import PatientDetailsTabs from './components/PatientDetailsTabs';
import PatientInfo from './components/PatientInfo';
import PatientRoomUsers from './components/PatientRoomUsers';

const PatientView = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const hasAccess = useRoles();
  const { mpi } = useParams();
  const [duplicatePatientResult, setDuplicatePatientResult] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);

  const patientInfo = useSelector(
    ({ patient }) =>
      pick(patient[mpi], 'firstName', 'lastName', 'status', 'email', 'dob', 'gender'),
    shallowEqual
  );
  const fullName =
    patientInfo?.firstName &&
    trim(`${patientInfo?.lastName}${patientInfo?.lastName ? ',' : ''} ${patientInfo?.firstName}`);

  const duplicatePatientRecords = useSelector(
    ({ patient }) => patient[mpi]?.duplicatePatientRecords
  );

  const partners = useSelector(({ auth, partner }) => partner[auth.currentTenantId]);
  const isEmptyPartners = isEmpty(partners);

  const initials =
    `${capitalize(first(patientInfo?.firstName))}${capitalize(first(patientInfo?.lastName))}` ||
    undefined;
  const avatarBgColor =
    patientInfo?.firstName && stc(`${fullName}${patientInfo.dob}${patientInfo.email}`);

  const statusBgColor = lighten(
    {
      OPEN: theme.palette.info.main,
      ONBOARDED: theme.palette.warning.main,
      QUALIFIED: theme.palette.success.main,
      UNQUALIFIED: theme.palette.error.main,
      DEACTIVATED: theme.palette.error.main,
      PROFILE_INCOMPLETE: theme.palette.error.main,
    }[patientInfo?.status] || '#FFF',
    0.7
  );

  const closeDialog = () => setShowDialog(false);

  const openDialog = () => setShowDialog(true);

  const handleOpenHistoryModal = () => setHistoryModalOpen(true);

  const handleCloseHistoryModal = () => setHistoryModalOpen(false);

  useEffect(() => {
    document.title = patientInfo?.firstName ? `${fullName} - SmartOS` : 'SmartOS';
  }, [fullName, patientInfo?.firstName]);

  useEffect(() => {
    dispatch(getPatient({ mpi })).then(() => {
      dispatch(getDuplicatePatients({ patientId: mpi }));
    });
  }, [dispatch, mpi]);

  useEffect(() => {
    if (isEmptyPartners) {
      dispatch(getPartners());
    }
  }, [dispatch, isEmptyPartners]);

  useEffect(() => {
    if (mpi) {
      dispatch(recordAuditEvent({ eventType: 'VIEW_PATIENT_PROFILE', patientId: mpi }));
    }
  }, [mpi, dispatch]);

  useEffect(() => {
    setDuplicatePatientResult(duplicatePatientRecords);
  }, [duplicatePatientRecords]);

  return (
    <Grid container direction='column' gap={4} sx={{ px: 2, pb: 2, position: 'relative' }}>
      {/* Patient Room Users - Show who's currently viewing this patient */}
      <PatientRoomUsers patientId={mpi} />

      <Grid item sx={{ minHeight: 40 }} container>
        <Grid item sx={{ display: 'inline-flex', flexGrow: 1, alignItems: 'center' }}>
          {fullName && (
            <>
              <Typography component='h1' variant='h4' sx={{ fontWeight: '500', mr: 2 }}>
                {fullName}
              </Typography>
              <Chip
                size='medium'
                label={capitalize(patientInfo?.status?.replace(/_/g, ' '))}
                sx={{
                  backgroundColor: statusBgColor || '#000',
                  color: theme.palette.getContrastText(statusBgColor || '#000'),
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  px: 0.5,
                  py: 2,
                }}
              />

              {hasAccess.viewPatientHistoryTrack && (
                <IconButton
                  size='small'
                  onClick={handleOpenHistoryModal}
                  sx={{ ml: 1, color: theme.palette.primary.main }}
                  title='View Patient History'
                >
                  <VisibilityIcon />
                </IconButton>
              )}

              {!isEmpty(duplicatePatientResult) && (
                <Badge
                  color='error'
                  variant='dot'
                  invisible={isEmpty(duplicatePatientResult)}
                  sx={{ ml: '10px' }}
                >
                  <WarningAmberIcon onClick={openDialog} />

                  <DuplicatePatientComparisonDialog
                    onClose={closeDialog}
                    showDialog={showDialog}
                    patients={duplicatePatientRecords}
                    mpi={mpi}
                  />
                </Badge>
              )}
            </>
          )}
          {!fullName && <Skeleton variant='rounded' width={250} height={40} animation='wave' />}
        </Grid>
        {hasAccess.onboardPatient && (
          <Grid item sx={{ pr: 2 }}>
            <OnboardingButton mpi={mpi} />
          </Grid>
        )}
      </Grid>

      <Grid item container>
        <Grid item sx={{ minWidth: 200, pb: 2 }} xs={12} md={1}>
          <Avatar
            sx={{
              bgcolor: avatarBgColor,
              color: theme.palette.getContrastText(avatarBgColor || '#000'),
              width: theme.spacing(20),
              height: theme.spacing(20),
              boxShadow: '1px 2px 7px grey',
              fontSize: '5.5em',
              letterSpacing: '0.3rem',
              m: 'auto',
            }}
            alt={patientInfo?.firstName}
          >
            {initials}
          </Avatar>

          {hasAccess.editPatientProfile && (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <EditPatientButton mpi={mpi} />
            </Box>
          )}
        </Grid>

        {/* Fixed height: seven label/value rows. A long partner list scrolls inside the card. */}
        <Grid item component={Paper} sx={{ px: 3, py: 2.5, flexGrow: 1, height: 356, minWidth: 0 }}>
          <PatientInfo mpi={mpi} />
        </Grid>
      </Grid>

      <Grid item>
        <PatientDetailsTabs />
      </Grid>

      {fullName && (
        <PatientHistoryTrackModal
          open={historyModalOpen}
          onClose={handleCloseHistoryModal}
          mpi={mpi}
          patientName={fullName}
        />
      )}
    </Grid>
  );
};

export default PatientView;
